import {
  type AnalyticsHistoryFilters,
  analyticsHistorySchema,
} from "@server/types/analyticsReportTypes";
import { useQuery } from "@tanstack/react-query";
import { useRequiredOrgId } from "@/hooks/useRequiredOrgId";
import { queryKeys } from "@/lib/query-keys";
import { apiGet, createOrgApiUrl } from "@/utils/apiUtils";

/** Independent history identity: no chart granularity or aggregate pagination enters this query. */
export type AnalyticsHistoryParams = AnalyticsHistoryFilters & {
  from: string;
  to: string;
};

/** Fetches one cursor page or exact ID without rebuilding aggregates. Cache entries never cross organizations or filters; new pages do not display the previous page's rows. */
export function useAnalyticsHistory(params: AnalyticsHistoryParams) {
  const orgId = useRequiredOrgId();
  return useQuery({
    queryKey: queryKeys.analytics.history(orgId, params),
    queryFn: async ({ signal }) => {
      const search = new URLSearchParams();
      for (const [key, value] of Object.entries(params))
        if (value !== undefined) search.set(key, value);
      const response = await apiGet<{ data: unknown }>(
        `${createOrgApiUrl(orgId, "analytics", "history")}?${search}`,
        { signal, timeout: 20_000 },
      );
      return analyticsHistorySchema.parse(response.data);
    },
    staleTime: 60_000,
    gcTime: 30 * 60_000,
  });
}
